import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { formatDateToLocalString } from '../utils/dateUtils';

// Runtime config desteği: window.__APP_CONFIG__ build sonrası güncellenebilir
const getApiBaseUrl = () => {
  // Önce runtime config'i kontrol et (build sonrası güncellenebilir)
  if (typeof window !== 'undefined' && window.__APP_CONFIG__ && window.__APP_CONFIG__.API_BASE_URL) {
    return window.__APP_CONFIG__.API_BASE_URL;
  }
  // Sonra build zamanı değerini kullan
  return API_BASE_URL;
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add token if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // FormData gönderirken Content-Type'ı kaldır (Axios otomatik multipart/form-data ekler)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 404 hatalarını sessizce geç (vekalet, belge vb. için normal)
    if (error.response?.status === 404) {
      const requestUrl = error.config?.url || '';
      // Vekalet ve belge endpoint'leri için 404 normal, sessizce geç
      if (requestUrl.includes('/Vekalet/case-file/') || 
          requestUrl.includes('/Document/case-file/')) {
        // Bu hataları sessizce geç, console'a yazdırma
        // Error'u özel bir flag ile işaretle ki console'a yazılmasın
        error.silent = true;
        return Promise.reject(error);
      }
    }
    
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const requestUrl = error.config?.url || '';
      
      // CaseFiles sayfasında (form açılırken veya dosya listesi yüklenirken) 401 hatası alınırsa
      // Token'ı temizleme, login'e yönlendirme yapma, sadece hata döndür (form açılmaya devam etsin)
      if (currentPath === '/case-files' || currentPath.startsWith('/case-files')) {
        // CaseFiles sayfasındayız (liste veya form açılırken)
        // Bu durumda login'e yönlendirme yapma, sadece hata döndür
        // Token geçersizse bile form açılsın, kullanıcı hatayı görsün
        // Not: Token'ı temizleme çünkü kullanıcı hala sayfada olabilir
        return Promise.reject(error);
      }
      
      // Dosya detay sayfasında belgeler yüklenirken 401 hatası alınabilir
      // Bu durumda token'ı temizleme (belgeler opsiyonel)
      if (currentPath.startsWith('/case-files/')) {
        // Dosya detay sayfasındayız
        // Sadece Document/case-file endpoint'i için token'ı temizleme (belgeler opsiyonel)
        if (requestUrl.includes('/Document/case-file/')) {
          // Belgeler yüklenirken 401 - token'ı temizleme, belgeler opsiyonel
          return Promise.reject(error);
        }
        // Diğer endpoint'ler için (CaseFile/getAll, Document/upload, vb.) token'ı temizle ve login'e yönlendir
        localStorage.removeItem('token');
        if (currentPath !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      // Diğer sayfalarda token'ı temizle ve login'e yönlendir
      // Hesap sayfaları API çağırmaz; yanlışlıkla 401 gelirse oturumu düşürme
      if (
        currentPath === '/tazminat-hesap' || currentPath.startsWith('/tazminat-hesap') ||
        currentPath === '/yasal-faiz-hesap' || currentPath.startsWith('/yasal-faiz-hesap') ||
        currentPath === '/makbuz-hesapla' || currentPath.startsWith('/makbuz-hesapla')
      ) {
        return Promise.reject(error);
      }

      localStorage.removeItem('token');
      if (currentPath !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password, username) => {
    // Backend hem Email hem Username ile arama yapıyor
    const requestBody = {
      Password: password
    };
    
    if (email) {
      requestBody.Email = email;
    }
    if (username) {
      requestBody.Username = username;
    }
    
    try {
      const response = await api.post('/UserAuth/login', requestBody);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      
      // Detaylı hata mesajlarını göster
      if (error.response?.data?.errors) {
        const errorMessages = Array.isArray(error.response.data.errors) 
          ? error.response.data.errors.join(', ')
          : JSON.stringify(error.response.data.errors);
        console.error('Validation errors:', errorMessages);
      }
      
      throw error;
    }
  },
  /** Bu cihaz icin Face ID anahtari (oturum degil; yalnizca yeni oturum acar). */
  faceIdAnahtar: async () => (await api.post('/UserAuth/faceid-anahtar')).data.anahtar,
  /** Face ID anahtariyla yeni oturum; { token } doner. */
  faceIdGiris: async (anahtar) => (await api.post('/UserAuth/faceid-giris', { anahtar })).data,
  /** Giris ekranindan teshis satiri (oturum gerekmez). Hata yutulur. */
  girisTani: async (metin) => {
    try { await api.post('/UserAuth/giris-tani', { metin }); } catch (e) { /* yoksay */ }
  },
  register: async (userData) => {
    try {
      const response = await api.post('/UserAuth/save', userData);
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      
      // Detaylı hata mesajlarını göster
      if (error.response?.data?.errors) {
        const errorMessages = Array.isArray(error.response.data.errors) 
          ? error.response.data.errors.join(', ')
          : JSON.stringify(error.response.data.errors);
        console.error('Validation errors:', errorMessages);
      }
      
      throw error;
    }
  }
};

// User API
export const userAPI = {
  getAll: async () => {
    const response = await api.get('/User/all-users');
    return response.data;
  },
  getUserCount: async () => {
    const response = await api.get('/User/count');
    return response.data;
  },
  getUserLoginHistory: async (userId) => {
    const response = await api.get(`/User/login-history/${userId}`);
    return response.data;
  },
  deleteUser: async (userId) => {
    const response = await api.delete(`/User/${userId}`);
    return response.data;
  },
  updateProfile: async (userData) => {
    const response = await api.put('/User/update', userData);
    return response.data;
  },
  updateTitle: async ({ UserId, Title }) => {
    const response = await api.put('/User/update-title', { UserId, Title });
    return response.data;
  },
  /** @deprecated updateTitle kullanın — Role alanına yazmaz, ünvan günceller */
  updateRole: async ({ UserId, Role }) => {
    const response = await api.put('/User/update-title', { UserId, Title: Role });
    return response.data;
  },
  getProfile: async () => {
    const response = await api.get('/User/current');
    return response.data;
  },
  /** Kullanicinin sifresini sifirlar — yalnizca BARAN yetkili. */
  resetPassword: async ({ UserId, YeniSifre }) => {
    const response = await api.put('/User/reset-password', { UserId, YeniSifre });
    return response.data;
  }
};

// Case File API
export const caseFileAPI = {
  getAll: async (ozel = false) => {
    const response = await api.get('/CaseFile/all', { params: { ozel: !!ozel } });
    return response.data;
  },
  getById: async (caseFileId) => {
    const response = await api.get(`/CaseFile/${caseFileId}`);
    return response.data;
  },
  create: async (caseFileData) => {
    // Token is automatically added via interceptor
    
    try {
      const response = await api.post('/CaseFile/create', caseFileData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('API create error:', error);
      console.error('Request config:', error.config);
      if (error.config?.data) {
        console.error('Request data:', error.config.data);
      }
      // Backend'den gelen hata mesajını göster
      if (error.response?.data?.message) {
        const errorMessage = error.response.data.message;
        console.error('Backend error message:', errorMessage);
        throw new Error(errorMessage);
      }
      throw error;
    }
  },
  update: async (caseFileData) => {
    // Token is automatically added via interceptor
    const response = await api.put(`/CaseFile`, caseFileData);
    return response.data;
  },
  setDurumIsaret: async (caseFileId, markType, value) => {
    // markType: 'durusma' veya 'tahkim'
    const response = await api.put('/CaseFile/durum-isaret', {
      CaseFileId: caseFileId,
      MarkType: markType,
      Value: value
    });
    return response.data;
  },
  updateIcraBilgileri: async (caseFileId, data) => {
    // data: { TakipAcilisMiktari, TakipTarihi, IcraFaizTuru, IcraFaizOrani,
    //         IcraFaizBaslangicTarihi, IcraTahsilAsamasi, IcraVekaletUcretiManuel }
    const id = parseInt(caseFileId, 10);
    const response = await api.put('/CaseFile/icra-bilgileri', {
      CaseFileId: id,
      ...data
    });
    return response.data;
  },
  updateStatusDavaTuru: async (caseFileId, { Status, DavaTuru, IcraKonusu, SozlesmeOraniKdvDahil }) => {
    const response = await api.put('/CaseFile/status-dava-turu', {
      CaseFileId: caseFileId,
      ...(Status !== undefined ? { Status } : {}),
      ...(DavaTuru !== undefined ? { DavaTuru } : {}),
      ...(IcraKonusu !== undefined ? { IcraKonusu } : {}),
      ...(SozlesmeOraniKdvDahil !== undefined ? { SozlesmeOraniKdvDahil } : {})
    });
    return response.data;
  },
  updateHesapDetayi: async (caseFileId, hesapDetayiJson) => {
    const response = await api.put('/CaseFile/hesap-detayi', {
      CaseFileId: caseFileId,
      HesapDetayiJson: typeof hesapDetayiJson === 'string'
        ? hesapDetayiJson
        : JSON.stringify(hesapDetayiJson ?? [])
    });
    return response.data;
  },
  updateKesinlesmeTarihi: async (caseFileId, kesinlesmeTarihi) => {
    const response = await api.put('/CaseFile/kesinlesme-tarihi', {
      CaseFileId: caseFileId,
      KesinlesmeTarihi: kesinlesmeTarihi
    });
    return response.data;
  },
  delete: async (caseFileId) => {
    // Token is automatically added via interceptor
    const response = await api.delete(`/CaseFile/${caseFileId}`);
    return response.data;
  },
  acigaAl: async (caseFileId) => {
    const response = await api.post(`/CaseFile/${caseFileId}/aciga-al`);
    return response.data;
  },
  /** Dosyanın müvekkil portalında nasıl göründüğünü döner (personel yetkisiyle). */
  muvekkilOnizleme: async (caseFileId) => {
    const response = await api.get(`/Muvekkil/onizleme/${caseFileId}`);
    return response.data;
  },
  /** Dosyaya ait muvekkil hareket kayitlari (yalnizca personel). */
  muvekkilLoglari: async (caseFileId) => {
    const response = await api.get(`/Muvekkil/loglar/${caseFileId}`);
    return response.data;
  },
  /** Tum muvekkillerin hareket kayitlari (yalnizca yetkili personel). */
  muvekkilTumLoglari: async (gunSayisi = 30, sonBakis = null) => {
    const response = await api.get('/Muvekkil/tum-loglar', {
      params: { gunSayisi, ...(sonBakis ? { sonBakis } : {}) },
    });
    return response.data;
  },

  /** Goz dugmesi rozetleri icin kucuk ozet. */
  muvekkilOzet: async (caseFileId, sonBakis = null) => {
    const response = await api.get(`/Muvekkil/ozet/${caseFileId}`, {
      params: sonBakis ? { sonBakis } : undefined,
    });
    return response.data;
  },
  cleanupDeletedFilesData: async () => {
    const response = await api.post('/CaseFile/cleanup-deleted-files-data');
    return response.data;
  },
  link: async (caseFileId1, caseFileId2) => {
    const response = await api.post('/CaseFile/link', {
      CaseFileId1: caseFileId1,
      CaseFileId2: caseFileId2
    });
    return response.data;
  },
  unlink: async (caseFileId1, caseFileId2) => {
    const response = await api.post('/CaseFile/unlink', {
      CaseFileId1: caseFileId1,
      CaseFileId2: caseFileId2
    });
    return response.data;
  }
};

// Archive Request API
export const archiveRequestAPI = {
  getAll: async () => {
    const response = await api.get('/ArchieveRequest/all');
    return response.data;
  },
  getArchivedFiles: async () => {
    const response = await api.get('/ArchieveRequest/archived-files');
    return response.data;
  },
  sendRequest: async (caseFileId) => {
    const response = await api.post(`/ArchieveRequest/send?caseFileId=${caseFileId}`);
    return response.data;
  },
  create: async (caseFileId) => {
    const response = await api.post('/ArchieveRequest/create', { caseFileId });
    return response.data;
  },
  approve: async (requestId) => {
    const response = await api.post(`/ArchieveRequest/approve?requesId=${requestId}`);
    return response.data;
  },
  reject: async (requestId) => {
    const response = await api.post(`/ArchieveRequest/reject?requesId=${requestId}`);
    return response.data;
  }
};

// Task API
export const noteAPI = {
  getMyNotes: async () => {
    const response = await api.get('/Note/my-notes');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/Note/${id}`);
    return response.data;
  },
  getByDate: async (date) => {
    const response = await api.get(`/Note/by-date/${formatDateToLocalString(date)}`);
    return response.data;
  },
  getByCaseFileId: async (caseFileId) => {
    const response = await api.get(`/Note/by-case-file/${caseFileId}`);
    return response.data;
  },
  create: async (dto) => {
    const response = await api.post('/Note/create', dto);
    return response.data;
  },
  update: async (id, dto) => {
    const response = await api.put(`/Note/update/${id}`, dto);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/Note/${id}`);
    return response.data;
  }
};

export const taskAPI = {
  create: async (taskData) => {
    const response = await api.post('/Task/create', taskData);
    return response.data;
  },
  update: async (taskData) => {
    const response = await api.put('/Task/update', taskData);
    return response.data;
  },
  reject: async (taskId, rejectionReason, newDueDate) => {
    const response = await api.post('/Task/reject', {
      TaskId: taskId,
      RejectionReason: rejectionReason,
      NewDueDate: newDueDate
    });
    return response.data;
  },
  getAll: async () => {
    const response = await api.get('/Task/all');
    return response.data;
  },
  getMyTasks: async () => {
    const response = await api.get('/Task/my-tasks');
    return response.data;
  },
  getById: async (taskId) => {
    const response = await api.get(`/Task/${taskId}`);
    return response.data;
  },
  getByCaseFileId: async (caseFileId) => {
    const response = await api.get(`/Task/by-casefile/${caseFileId}`);
    return response.data;
  },
  delete: async (taskId) => {
    const response = await api.delete(`/Task/${taskId}`);
    return response.data;
  }
};

// Document API
export const documentAPI = {
  upload: async (caseFileId, file, description, documentType = 0, faturaTarihi = null, tutar = null, options = {}) => {
    const { onUploadProgress, signal, maxRetries = 2, makbuz } = options || {};
    const formData = new FormData();
    formData.append('caseFileId', caseFileId);
    // Makbuzdan okunan ödenecek KDV ve brüt ücret (isteğe bağlı)
    if (makbuz?.odenecekKdv != null) formData.append('odenecekKdv', String(makbuz.odenecekKdv));
    if (makbuz?.brutUcret != null) formData.append('brutUcret', String(makbuz.brutUcret));
    // Capacitor / iOS: File adı ve MIME kesin olsun
    const safeName = file?.name || `belge_${Date.now()}`;
    const safeType = file?.type || 'application/octet-stream';
    const uploadBlob = (file instanceof Blob)
      ? file
      : new Blob([file], { type: safeType });
    formData.append('file', uploadBlob, safeName);
    formData.append('documentType', documentType);
    if (description) {
      formData.append('description', description);
    }
    if (faturaTarihi) {
      formData.append('faturaTarihi', faturaTarihi);
    }
    if (tutar !== null && tutar !== undefined) {
      formData.append('tutar', tutar.toString());
    }
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token bulunamadı. Lütfen tekrar giriş yapın.');
    }

    const isRetryableNetworkError = (err) => {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return false;
      if (err?.response?.status === 401 || err?.response?.status === 413) return false;
      if (err?.response?.status >= 400 && err?.response?.status < 500) return false;
      return !err?.response || err?.code === 'ERR_NETWORK' || err?.message === 'Network Error'
        || err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
    };

    let lastError = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0 && typeof onUploadProgress === 'function') {
          onUploadProgress({ loaded: 0, total: uploadBlob.size || 0, progress: 0, retrying: true, attempt });
        }
        const response = await api.post('/Document/upload', formData, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 0,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          signal,
          onUploadProgress: typeof onUploadProgress === 'function'
            ? (evt) => {
                const total = evt.total || uploadBlob.size || 0;
                const loaded = evt.loaded || 0;
                const progress = total > 0 ? Math.min(99, Math.round((loaded / total) * 100)) : 0;
                onUploadProgress({ loaded, total, progress, retrying: false, attempt });
              }
            : undefined
        });
        if (typeof onUploadProgress === 'function') {
          onUploadProgress({ loaded: uploadBlob.size || 0, total: uploadBlob.size || 0, progress: 100, retrying: false, attempt });
        }
        return response.data;
      } catch (err) {
        lastError = err;
        if (attempt >= maxRetries || !isRetryableNetworkError(err)) {
          throw err;
        }
        // Kısa bekleme sonra yeniden dene (büyük dosya / kopuk bağlantı)
        await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
      }
    }
    throw lastError;
  },
  getByCaseFile: async (caseFileId) => {
    // Token'ı manuel olarak ekle - interceptor bazen çalışmayabilir
    const token = localStorage.getItem('token');
    
    if (!token) {
      return []; // Belgeler opsiyonel, boş array döndür
    }
    
    const response = await api.get(`/Document/case-file/${caseFileId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },
  /** Belgeyi müvekkil portalına açar/kapatır. */
  setMuvekkilGorunurluk: async (documentId, gorebilir) => {
    const response = await api.put(`/Document/${documentId}/muvekkil-gorunurluk`, { gorebilir });
    return response.data;
  },

  /** Belgenin görünen adını değiştirir (uzantı sunucuda korunur). */
  rename: async (documentId, fileName) => {
    const response = await api.put(`/Document/${documentId}/name`, { fileName });
    return response.data;
  },

  /** Makbuzun ödenecek KDV'si ve brüt ücreti (elle düzeltme / eski makbuzu PDF'inden okuma). */
  makbuzKdv: async (documentId, { odenecekKdv, brutUcret }) => {
    const response = await api.put(`/Document/${documentId}/makbuz-kdv`, {
      OdenecekKdv: odenecekKdv ?? null,
      BrutUcret: brutUcret ?? null,
    });
    return response.data;
  },
  updateType: async (documentId, { documentType, faturaTarihi, tutar }) => {
    const response = await api.put(`/Document/${documentId}/type`, {
      DocumentType: documentType,
      FaturaTarihi: faturaTarihi || null,
      Tutar: tutar != null && tutar !== '' ? Number(tutar) : null
    });
    return response.data;
  },
  getAll: async () => {
    const response = await api.get('/Document/all');
    return response.data;
  },
  getInvoices: async () => {
    const response = await api.get('/Document/invoices');
    return response.data;
  },
  getIbranameMuvafakatnameler: async () => {
    const response = await api.get('/Document/ibraname-muvafakatname');
    return response.data;
  },
  uploadInvoice: async (file, description, faturaTarihi, tutar, makbuz = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('faturaTarihi', faturaTarihi);
    formData.append('tutar', tutar.toString());
    if (makbuz?.odenecekKdv != null) formData.append('odenecekKdv', String(makbuz.odenecekKdv));
    if (makbuz?.brutUcret != null) formData.append('brutUcret', String(makbuz.brutUcret));
    if (description) {
      formData.append('description', description);
    }
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token bulunamadı. Lütfen tekrar giriş yapın.');
    }
    
    const response = await api.post('/Document/upload-invoice', formData, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      timeout: 0,
      maxContentLength: Infinity,
      maxBodyLength: Infinity
    });
    return response.data;
  },
  getMatbuDilekceler: async () => {
    const response = await api.get('/Document/matbu');
    return response.data;
  },
  searchEvrak: async (q) => {
    const response = await api.get('/Document/search-evrak', { params: { q } });
    return response.data;
  },
  uploadMatbu: async (file, description) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Token bulunamadı. Lütfen tekrar giriş yapın.');
    }

    const response = await api.post('/Document/upload-matbu', formData, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      timeout: 0,
      maxContentLength: Infinity,
      maxBodyLength: Infinity
    });
    return response.data;
  },
  delete: async (documentId) => {
    const response = await api.delete(`/Document/${documentId}`);
    return response.data;
  },
  download: async (documentId) => {
    const token = localStorage.getItem('token');
    const response = await api.get(`/Document/${documentId}/download`, {
      responseType: 'blob',
      timeout: 0,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    let fileName = null;
    const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
    if (disposition) {
      const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const plain = /filename=\"?([^\";]+)\"?/i.exec(disposition);
      if (star?.[1]) fileName = decodeURIComponent(star[1]);
      else if (plain?.[1]) fileName = plain[1];
    }
    return {
      blob: response.data,
      contentType: response.headers?.['content-type'] || response.data?.type || null,
      fileName
    };
  },
  reorder: async (caseFileId, documentIds) => {
    const response = await api.put(`/Document/case-file/${caseFileId}/reorder`, {
      CaseFileId: caseFileId,
      DocumentIds: documentIds
    });
    return response.data;
  },
  generateSigortaBasvuru: async (caseFileId) => {
    const token = localStorage.getItem('token');
    const response = await api.post(`/Document/sigorta-basvuru/${caseFileId}`, null, {
      responseType: 'blob',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response;
  },
  generateTahkimBasvuru: async (caseFileId) => {
    const token = localStorage.getItem('token');
    const response = await api.post(`/Document/tahkim-basvuru/${caseFileId}`, null, {
      responseType: 'blob',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response;
  }
};

// Referans API
export const referansAPI = {
  getAll: async () => {
    const response = await api.get('/Referans/all');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/Referans/${id}`);
    return response.data;
  },
  create: async (referansData) => {
    const response = await api.post('/Referans/create', referansData);
    return response.data;
  },
  update: async (referansData) => {
    const response = await api.put('/Referans/update', referansData);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/Referans/${id}`);
    return response.data;
  },
  getCaseFilesByReferans: async (referansId) => {
    const response = await api.get(`/CaseFile/by-referans/${referansId}`);
    return response.data;
  }
};

export const caseFileLogAPI = {
  getAll: async () => {
    const response = await api.get('/CaseFileLog/all');
    return response.data;
  },
  getByCaseFileId: async (caseFileId) => {
    const response = await api.get(`/CaseFileLog/by-casefile/${caseFileId}`);
    return response.data;
  }
};

export const masrafLogAPI = {
  getAll: async () => {
    const response = await api.get('/MasrafLog/all');
    return response.data;
  },
  getByMasrafId: async (masrafId) => {
    const response = await api.get(`/MasrafLog/by-masraf/${masrafId}`);
    return response.data;
  },
  getByCaseFileId: async (caseFileId) => {
    const response = await api.get(`/MasrafLog/by-casefile/${caseFileId}`);
    return response.data;
  }
};

export const vekaletAPI = {
  getAll: async () => {
    const response = await api.get('/Vekalet/all');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/Vekalet/${id}`);
    return response.data;
  },
  getByCaseFileId: async (caseFileId) => {
    try {
      const response = await api.get(`/Vekalet/case-file/${caseFileId}`, {
        validateStatus: function (status) {
          // 404 durumunda bile hata fırlatma, response'u döndür
          return status < 500; // 500 ve üzeri hatalar için hata fırlat
        }
      });
      // 404 ise null döndür
      if (response.status === 404) {
        return null;
      }
      return response.data;
    } catch (error) {
      // 404 hatası normal (vekalet yok), sessizce null döndür
      if (error.response?.status === 404 || error.silent) {
        return null;
      }
      // Diğer hataları fırlat
      throw error;
    }
  },
  create: async (dto) => {
    const response = await api.post('/Vekalet/create', dto);
    return response.data;
  },
  update: async (id, dto) => {
    const response = await api.put(`/Vekalet/update/${id}`, dto);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/Vekalet/${id}`);
    return response.data;
  }
};

export const masrafAPI = {
  getAll: async () => {
    const response = await api.get('/Masraf/all');
    return response.data;
  },
  getById: async (masrafId) => {
    const response = await api.get(`/Masraf/${masrafId}`);
    return response.data;
  },
  getByCaseFileId: async (caseFileId) => {
    const response = await api.get(`/Masraf/by-casefile/${caseFileId}`);
    return response.data;
  },
  create: async (masrafData) => {
    const response = await api.post('/Masraf/create', masrafData);
    return response.data;
  },
  update: async (masrafData) => {
    const response = await api.put('/Masraf', masrafData);
    return response.data;
  },
  delete: async (masrafId) => {
    const response = await api.delete(`/Masraf/${masrafId}`);
    return response.data;
  }
};

export const kasaAPI = {
  getSummary: async (year = null, month = null) => {
    const params = {};
    if (year !== null && month !== null) {
      params.year = year;
      params.month = month;
    }
    const response = await api.get('/Kasa/summary', { params });
    return response.data;
  },
  createMasraf: async (masrafData) => {
    const response = await api.post('/Kasa/masraf/create', masrafData);
    return response.data;
  }
};

/** Dosyalardan gruplanan muvekkil listesi. */
export const muvekkilListeAPI = {
  liste: async () => (await api.get('/Muvekkiller')).data,
  detay: async (tc) => (await api.get('/Muvekkiller/' + encodeURIComponent(tc))).data
};

/** Gelen e-postalarin okunmasi ve is listesine aktarilmasi. */
export const epostaAPI = {
  hesap: async () => (await api.get('/Eposta/hesap')).data,
  hesapKaydet: async (h) => (await api.post('/Eposta/hesap', h)).data,
  test: async () => (await api.post('/Eposta/hesap/test')).data,
  oku: async (enFazla = 40) => (await api.post('/Eposta/oku', null, { params: { enFazla }, timeout: 120000 })).data,
  yazismalar: async (durum = 'bekliyor', adet = 60, gun = 0) =>
    (await api.get('/Eposta/yazismalar', { params: { durum, adet, gun } })).data,
  onayla: async (kayit) => (await api.post('/Eposta/onayla', kayit)).data,
  yoksay: async (id) => (await api.post(`/Eposta/yoksay/${id}`)).data,
  /** Birden fazla postayi tek islemde yoksayar. */
  yoksayToplu: async (idler) => (await api.post('/Eposta/yoksay-toplu', { Idler: idler })).data,
  /** Postalari listeden tamamen kaldirir (kayit arsivde kalir). */
  silToplu: async (idler) => (await api.post('/Eposta/sil-toplu', { Idler: idler })).data,
  /** Kenar cubugu rozeti icin bekleyen sayisi + son kontrol zamani. */
  bekleyenSayi: async (sonBakis = null) => (await api.get('/Eposta/bekleyen-sayi', { params: { sonBakis } })).data
};

/** Vekaletnameden muvekkil bilgisi okuma. */
/**
 * PERSONEL: müvekkile tek kullanımlık giriş kodu üretir.
 * Şifre bcrypt ile saklandığı için geri okunamaz; giremeyen müvekkile
 * yeni kod üretilir, o da kodla girip kendi şifresini belirler.
 */
export const muvekkilYonetimAPI = {
  girisKoduUret: async (tc) =>
    (await api.post(`/Muvekkil/giris-kodu/${encodeURIComponent(tc)}`)).data,

  /* Kod üretmeden önce sorulur: müvekkilin kendi şifresi var mı?
     Varsa kod üretmek onu geçersiz kılacağı için arayüz uyarır. */
  girisDurumu: async (tc) =>
    (await api.get(`/Muvekkil/giris-durumu/${encodeURIComponent(tc)}`)).data,

  /* Müvekkilin uygulamaya ve siteye girişini durdurur / yeniden açar.
     Yanıt güncel giriş durumudur (girisDurumu ile aynı biçim). */
  girisDurdur: async (tc) =>
    (await api.post(`/Muvekkil/giris-durdur/${encodeURIComponent(tc)}`)).data,
  girisAc: async (tc) =>
    (await api.post(`/Muvekkil/giris-ac/${encodeURIComponent(tc)}`)).data,
};

export const vekaletnameAPI = {
  oku: async (caseFileId) =>
    (await api.get(`/Vekaletname/oku/${caseFileId}`, { timeout: 180000 })).data,
  okuKaydet: async (caseFileId, uzerineYaz = false) =>
    (await api.post('/Vekaletname/oku-kaydet', { CaseFileId: caseFileId, UzerineYaz: uzerineYaz }, { timeout: 180000 })).data,
  muvekkilOku: async (tc) =>
    (await api.post(`/Vekaletname/muvekkil/${encodeURIComponent(tc)}`, null, { timeout: 300000 })).data,
  toplu: async (enFazla = 25) =>
    (await api.post('/Vekaletname/toplu', null, { params: { enFazla }, timeout: 600000 })).data,
  telefonlariDuzelt: async () =>
    (await api.post('/Vekaletname/telefonlari-duzelt', null, { timeout: 120000 })).data,

  // --- Toplu tarama ---
  taramaBaslat: async (hepsi = false) =>
    (await api.post('/Vekaletname/tarama-baslat', null, { params: { hepsi } })).data,
  taramaDurum: async () => (await api.get('/Vekaletname/tarama-durum')).data,
  uyusmazliklar: async (durum = 'bekliyor') =>
    (await api.get('/Vekaletname/uyusmazliklar', { params: { durum } })).data,
  uyusmazlikKarar: async (id, onayla) =>
    (await api.post('/Vekaletname/uyusmazlik-karar', { Id: id, Onayla: onayla })).data,

  /** Yeni dosya acarken: yuklenen vekaletnameyi okur, hicbir sey kaydetmez. */
  okuYuklenen: async (dosya) => {
    const form = new FormData();
    form.append('dosya', dosya);
    const cevap = await api.post('/Vekaletname/oku-yuklenen', form, {
      timeout: 300000,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return cevap.data;
  }
};
/**
 * Arac hasar hesabi: TSB kasko deger listesi ve ruhsattan arac bilgisi.
 * Hicbir sey kaydetmez.
 */
export const aracBilgiAPI = {
  tsbYillar: async () => (await api.get('/AracBilgi/tsb/yillar')).data,
  tsbMarkalar: async (yil) => (await api.get('/AracBilgi/tsb/markalar', { params: { yil } })).data,
  tsbModeller: async (yil, markaId) =>
    (await api.get('/AracBilgi/tsb/modeller', { params: { yil, markaId } })).data,
  /** Kaza ayinin listesi ilk sorguda TSB'den indirilir (birkac saniye). */
  tsbDeger: async (yil, modelId, kazaTarihi) =>
    (await api.get('/AracBilgi/tsb/deger', { params: { yil, modelId, kazaTarihi: kazaTarihi || undefined }, timeout: 90000 })).data,
  ruhsatAdaylari: async (caseFileId) => (await api.get(`/AracBilgi/ruhsat/adaylar/${caseFileId}`)).data,
  ruhsatOku: async (caseFileId, belgeId = null) =>
    (await api.post('/AracBilgi/ruhsat/oku', { CaseFileId: caseFileId, BelgeId: belgeId }, { timeout: 180000 })).data,
  ruhsatOkuYuklenen: async (dosya) => {
    const form = new FormData();
    form.append('dosya', dosya);
    const cevap = await api.post('/AracBilgi/ruhsat/oku-yuklenen', form, {
      timeout: 180000,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return cevap.data;
  }
};

/**
 * Dosya detayindaki Arabuluculuk bolumu: belgelerden okuma ve kayit.
 * Belgeler'e arabuluculuk evraki (UDF) eklenince sunucu bos alanlari
 * kendisi doldurur; "oku" ayni okumayi elle baslatir.
 */
export const arabuluculukAPI = {
  adaylar: async (caseFileId) => (await api.get(`/Arabuluculuk/${caseFileId}/adaylar`)).data,
  /** Bos alanlari doldurur, dolu alanlardaki farklari dondurur; taranmis belgede OCR surebilir. */
  oku: async (caseFileId, belgeId = null) =>
    (await api.post(`/Arabuluculuk/${caseFileId}/oku`, { BelgeId: belgeId }, { timeout: 180000 })).data,
  /** alanlar: degisecek alanlarin adlari; listede olmayana dokunulmaz. */
  kaydet: async (caseFileId, alanlar, degerler) =>
    (await api.put(`/Arabuluculuk/${caseFileId}`, { Alanlar: alanlar, ...degerler })).data,
};

/** Baran'in kisisel giderleri — ofis kasasindan ayri. */
export const kisiselGiderAPI = {
  getAy: async (yil, ay) => {
    const response = await api.get('/KisiselGider', { params: { yil, ay } });
    return response.data;
  },
  kaydet: async (kayit) => {
    const response = await api.post('/KisiselGider', kayit);
    return response.data;
  },
  sil: async (id) => {
    const response = await api.delete(`/KisiselGider/${id}`);
    return response.data;
  },
  krediKaydet: async (kredi) => {
    const response = await api.post('/KisiselGider/kredi', kredi);
    return response.data;
  },
  krediOdeme: async (odeme) => {
    const response = await api.post('/KisiselGider/kredi/odeme', odeme);
    return response.data;
  },
  krediSil: async (id) => {
    const response = await api.delete(`/KisiselGider/kredi/${id}`);
    return response.data;
  }
};

export const masrafRequestAPI = {
  getAll: async () => {
    const response = await api.get('/MasrafRequest/all');
    return response.data;
  },
  getPending: async () => {
    const response = await api.get('/MasrafRequest/pending');
    return response.data;
  },
  getMyRequests: async () => {
    const response = await api.get('/MasrafRequest/my-requests');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/MasrafRequest/${id}`);
    return response.data;
  },
  create: async (dto) => {
    const response = await api.post('/MasrafRequest/create', dto);
    return response.data;
  },
  update: async (id, dto) => {
    const response = await api.put(`/MasrafRequest/update/${id}`, dto);
    return response.data;
  },
  approve: async (id) => {
    const response = await api.post(`/MasrafRequest/approve/${id}`);
    return response.data;
  },
  reject: async (id, rejectionReason) => {
    const response = await api.post(`/MasrafRequest/reject/${id}`, { RejectionReason: rejectionReason });
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/MasrafRequest/${id}`);
    return response.data;
  }
};

export const notificationAPI = {
  /** TESHIS (personel): telefonda bir ozellik neden calismadi. */
  tani: async (konu, deger) => {
    try { await api.post('/Notification/tani', { konu, deger }); } catch (e) { /* yoksay */ }
  },
  registerDevice: async (token, platform = 'ios') => {
    const response = await api.post('/Notification/register-device', { token, platform });
    return response.data;
  },
  getFeed: async () => {
    const response = await api.get('/Notification/feed');
    return response.data;
  }
};

export const usefulLinkAPI = {
  getAll: async () => {
    const response = await api.get('/UsefulLink/all');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/UsefulLink/${id}`);
    return response.data;
  },
  create: async (dto) => {
    const response = await api.post('/UsefulLink/create', dto);
    return response.data;
  },
  update: async (id, dto) => {
    const response = await api.put(`/UsefulLink/update/${id}`, dto);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/UsefulLink/${id}`);
    return response.data;
  }
};

export const jobAPI = {
  getAll: async () => {
    const response = await api.get('/Job/all');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/Job/${id}`);
    return response.data;
  },
  create: async (dto) => {
    const response = await api.post('/Job/create', dto);
    return response.data;
  },
  update: async (dto) => {
    const response = await api.put('/Job/update', dto);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/Job/${id}`);
    return response.data;
  },
  requestReview: async (id) => {
    const response = await api.post(`/Job/request-review/${id}`);
    return response.data;
  },
  getPendingRequests: async () => {
    const response = await api.get('/Job/pending-requests');
    return response.data;
  },
  approve: async (id) => {
    const response = await api.post(`/Job/approve/${id}`);
    return response.data;
  },
  reject: async (id, rejectionReason, newDueDate) => {
    const response = await api.post(`/Job/reject/${id}`, { 
      RejectionReason: rejectionReason,
      NewDueDate: newDueDate || null
    });
    return response.data;
  }
};

/* ============================================================
   Müvekkil portalı
   ------------------------------------------------------------
   Personel oturumundan TAMAMEN AYRI çalışır: token ayrı anahtarda
   saklanır ve bu çağrılar ana "api" örneğini kullanmaz — böylece
   personel interceptor'ları (401'de /login'e atma vb.) karışmaz.
   ============================================================ */

export const MUVEKKIL_TOKEN_KEY = 'muvekkilToken';
export const MUVEKKIL_BILGI_KEY = 'muvekkilBilgi';

const muvekkilClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

muvekkilClient.interceptors.request.use((config) => {
  const t = localStorage.getItem(MUVEKKIL_TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  if (config.data instanceof FormData) delete config.headers['Content-Type'];
  return config;
});

/**
 * "Son bakış" zamanları — bir ekranı en son ne zaman gördüğünüz.
 * Sunucuda kullanıcı başına saklanır; telefondan girince ya da önbellek
 * temizlenince rozetler sıfırlanmasın diye.
 */
export const bakisAPI = {
  getir: async () => (await api.get('/Bakis')).data,
  isaretle: async (ekran) => (await api.post('/Bakis', { Ekran: ekran })).data,
};

/** Bildirim merkezi — ust cubuktaki zil. */
export const bildirimAPI = {
  getir: async (sonBakis = null, adet = 40) =>
    (await api.get('/Bildirim', { params: { sonBakis, adet } })).data,
  sayi: async (sonBakis = null) =>
    (await api.get('/Bildirim/sayi', { params: { sonBakis } })).data,

  /* Okundu artık bildirim bazlı. Zaman karşılaştırmasına dayanan eski
     yöntemde ileri tarihli bildirimler (duruşma, vekalet) hiçbir zaman
     okunmuş sayılmıyor, işaretlense bile listede kalıyordu. */
  okundu: async (anahtarlar) =>
    (await api.post('/Bildirim/okundu', { anahtarlar })).data,
  hepsiOkundu: async () => (await api.post('/Bildirim/hepsi-okundu')).data,

  /* Postalar bildirim canindan ayrildi; cok sayida olduklari icin is
     gerektiren bildirimleri bastiriyorlardi. Artik canin yanindaki
     ayri posta kutusu dugmesinden goruluyorlar. */
  postaSayisi: async () => (await api.get('/Bildirim/posta-sayisi')).data,
};

export const ayarAPI = {
  whatsappGetir: async () => {
    const response = await api.get('/Ayar/whatsapp');
    return response.data;
  },
  whatsappKaydet: async (ayar) => {
    const response = await api.post('/Ayar/whatsapp', ayar);
    return response.data;
  },
  postaGetir: async () => (await api.get('/Ayar/posta')).data,
  postaKaydet: async (ayar) => (await api.post('/Ayar/posta', ayar)).data
};

export const muvekkilAPI = {
  login: async (tc, sifre) => {
    const { data } = await muvekkilClient.post('/Muvekkil/login', { tc, sifre });
    return data;
  },

  /** Bu cihaz icin Face ID anahtari (oturum degil; yalnizca yeni oturum acar). */
  faceIdAnahtar: async () => (await muvekkilClient.post('/Muvekkil/faceid-anahtar')).data.anahtar,
  /** Face ID anahtariyla yeni oturum; girisle ayni yaniti doner. */
  faceIdGiris: async (anahtar) => (await muvekkilClient.post('/Muvekkil/faceid-giris', { anahtar })).data,

  /** Portaldaki "Bildirimler" listesi ve okunmamis adedi. */
  bildirimler: async (adet = 50) => {
    const { data } = await muvekkilClient.get('/Muvekkil/bildirimler', { params: { adet } });
    return data;
  },

  /**
   * id verilmezse HEPSI okundu isaretlenir. documentId verilirse yalnizca
   * o belgeye ait bildirimler (telefondaki bildirime dokunulunca).
   * Sunucu ardindan uygulama simgesindeki sayiyi da gunceller.
   */
  bildirimOkundu: async (id = null, documentId = null) => {
    const govde = id ? { id } : (documentId ? { documentId } : {});
    const { data } = await muvekkilClient.post('/Muvekkil/bildirim-okundu', govde);
    return data;
  },

  /** TESHIS: telefonda bir ozellik neden calismadi -- sunucu kaydina yazilir. */
  tani: async (konu, deger) => {
    try { await muvekkilClient.post('/Muvekkil/tani', { konu, deger }); } catch (e) { /* yoksay */ }
  },

  /** Muvekkilin telefonunu bildirim icin kaydeder (yalnizca uygulamada). */
  cihazKaydet: async (token, platform = 'ios') => {
    const { data } = await muvekkilClient.post('/Muvekkil/cihaz-kaydet', { token, platform });
    return data;
  },

  iletisim: async () => {
    const { data } = await muvekkilClient.get('/Muvekkil/iletisim');
    return data;
  },
  /** Müvekkil ilk girişten sonra kendi şifresini belirler. */
  sifreBelirle: async (yeniSifre) => {
    const { data } = await muvekkilClient.post('/Muvekkil/sifre-belirle', { YeniSifre: yeniSifre });
    return data;
  },
  dosyalarim: async () => {
    const { data } = await muvekkilClient.get('/Muvekkil/dosyalarim');
    return data;
  },

  belgeler: async (caseFileId) => {
    const { data } = await muvekkilClient.get(`/Muvekkil/dosya/${caseFileId}/belgeler`);
    return data;
  },

  /** Müvekkile YAPILAN ödemeler: { toplam, adet, odemeler: [{asama, tutar, tarih}] } */
  /** Cikis kaydi (oturum zaten istemcide sonlanir; bu yalnizca log icin). */
  cikisKaydet: async () => {
    try { await muvekkilClient.post('/Muvekkil/cikis'); } catch (_) { /* log basarisiz olsa da cikis yapilir */ }
  },

  odemeler: async (caseFileId) => {
    const { data } = await muvekkilClient.get(`/Muvekkil/dosya/${caseFileId}/odemeler`);
    return data;
  },

  /**
   * Belge içeriğini getirir.
   * onizleme=true ise hareket kaydına "görüntüledi" olarak düşer; aksi halde
   * "indirdi". İkisi aynı ucu kullandığı için ayrım burada bildiriliyor.
   */
  /**
   * Akış adresi: video ve PDF için doğrudan tarayıcıya verilir.
   *
   * <video> ve pdf.js dosyayı kendileri ister ve Authorization başlığı
   * gönderemezler; bu yüzden jeton adres satırında taşınır. Sunucu bu ucu
   * parçalı (Range) sunar — video indirmeyi beklemeden oynar, büyük PDF'in
   * ilk sayfası tamamı inmeden açılır.
   */
  /**
   * Akis adresi jetonu SORGU DIZESINDE tasir; IIS sorgu dizesini gunluge
   * yazdigi icin buraya oturum jetonu (12 saat gecerli) KONULMAZ. Bunun
   * yerine sunucudan yalnizca BU BELGEYE ve birkac dakikaya bagli kisa
   * omurlu bir jeton alinir; gunluge dusen satir kisa surede ise yaramaz.
   */
  akisUrl: async (documentId) => {
    const res = await muvekkilClient.get(`/Muvekkil/belge/${documentId}/akis-jeton`);
    const jeton = res?.data?.jeton ?? res?.data?.Jeton ?? '';
    if (!jeton) throw new Error('Belge bağlantısı alınamadı.');
    const taban = getApiBaseUrl().replace(/\/+$/, '');
    return `${taban}/Muvekkil/belge/${documentId}/akis?jeton=${encodeURIComponent(jeton)}`;
  },

  belgeIndir: async (documentId, onizleme = false, onDownloadProgress = undefined) => {
    const res = await muvekkilClient.get(`/Muvekkil/belge/${documentId}/indir`, {
      responseType: 'blob',
      params: onizleme ? { onizleme: true } : undefined,
      // Buyuk belgelerde ilerleme gosterebilmek icin; zaman asimi da
      // kaldiriliyor, yoksa yavas baglantida yarida kesiliyor.
      timeout: 0,
      onDownloadProgress,
    });
    return { blob: res.data, contentType: res.headers['content-type'] };
  },

  belgeYukle: async (caseFileId, file, onUploadProgress) => {
    const fd = new FormData();
    fd.append('file', file, file?.name || 'belge');
    const { data } = await muvekkilClient.post(`/Muvekkil/dosya/${caseFileId}/belge-yukle`, fd, {
      onUploadProgress,
    });
    return data;
  },

  cikis: () => {
    // Çıkış kaydı için sunucuya haber ver; token temizlenmeden ÖNCE
    // gönderilmeli, yoksa istek yetkisiz kalır. Sonucu beklemiyoruz —
    // kayıt tutulamasa da çıkış her hâlükârda yapılmalı.
    try { muvekkilAPI.cikisKaydet(); } catch (_) { /* yoksay */ }
    localStorage.removeItem(MUVEKKIL_TOKEN_KEY);
    localStorage.removeItem(MUVEKKIL_BILGI_KEY);
    // Ana ekran widget'i da veri cekemesin.
    import('../utils/widgetKoprusu')
      .then((m) => m.widgetJetonuSil())
      .catch(() => {});
  },

  oturumVar: () => !!localStorage.getItem(MUVEKKIL_TOKEN_KEY),
};
